import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { MessageSummary } from '@/shared/contracts';
import { photoStage } from './photoStage';

type Props = {
  message: Pick<MessageSummary, 'message_id' | 'photo_url' | 'photo_status'>;
  scope: string;
  onRead?: () => void | Promise<boolean>;
  reading?: boolean;
};

/** Browser image state is independent of generation and letter delivery state. */
function PhotoImage({ url, paper, onRead, reading }: { url: string; paper: boolean; onRead?: () => void | Promise<boolean>; reading?: boolean }) {
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [expanded, setExpanded] = useState(false);
  const imageId = useId();
  const alive = useRef(true);
  const image = useRef<HTMLImageElement>(null);
  const manual = useRef({ busy: false, generation: 0 });
  const [readGeneration, setReadGeneration] = useState(0);
  const read = () => {
    if (!onRead || reading || manual.current.busy) return;
    manual.current.busy = true;
    const token = ++manual.current.generation;
    let result: void | Promise<boolean>;
    try { result = onRead(); } catch { manual.current.busy = false; return; }
    void Promise.resolve(result).then(success => {
      if (success !== true || !alive.current || manual.current.generation !== token) return;
      setExpanded(false); setState('loading'); setReadGeneration(current => current + 1);
    }).catch(() => undefined).finally(() => {
      if (manual.current.generation === token) manual.current.busy = false;
    });
  };
  const currentImage = (target: HTMLImageElement) => alive.current && target === image.current && target.isConnected;
  useLayoutEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  return (
    <figure className="ps-msg__photo ps-msg__attachment" data-image-state={state} data-expanded={expanded}>
      {state !== 'error' ? <img key={readGeneration} ref={image} id={imageId} src={url} alt={paper ? 'TA 寄来的纸质明信片' : '来信附图'} loading="lazy" onLoad={event => { if (currentImage(event.currentTarget)) setState(current => current === 'error' ? 'error' : 'loaded'); }} onError={event => { if (currentImage(event.currentTarget)) { setExpanded(false); setState('error'); } }} /> : null}
      <div className="ps-msg__photo-status" role="status" aria-live="polite">
        {state === 'loading' ? '附图加载中…' : state === 'error' ? '暂时无法显示这张附图，来信正文仍可阅读。' : null}
      </div>
      {paper ? <figcaption>一张纸质明信片</figcaption> : null}
      {state === 'loaded' ? <button type="button" className="ps-msg__photo-read ps-msg__photo-toggle048" aria-controls={imageId} aria-expanded={expanded} onClick={() => setExpanded(current => !current)}>{expanded ? '收起照片' : '展开照片'}</button> : null}
      {state === 'error' && onRead ? <button type="button" className="ps-msg__photo-read" disabled={reading} onClick={read}>重新读取消息</button> : null}
    </figure>
  );
}

/** 对话里照片的三态插图（ASSET-UI-009 第二版，I 10-09 生成，原字节在 public/ui-assets/ASSET-UI-009/v1/；PHOTO-ASK-FLOW-01）。纯装饰：alt 空、读屏不念，状态由下面那句字说。 */
const PENDING_ART: Partial<Record<string, string>> = { processing: '01-developing-v2', failed: '02-failed-v2', unknown: '03-pending-review' };
function PendingArt({ status }: { status: string | null | undefined }) {
  const name = status ? PENDING_ART[status] : undefined;
  if (!name) return null;
  const base = `/ui-assets/ASSET-UI-009/v1/${name}`;
  return <img className="ps-msg__photo-art" src={`${base}@320x240.webp`} srcSet={`${base}@320x240.webp 320w, ${base}@480x360.webp 480w`} sizes="160px" width={160} height={120} alt="" aria-hidden="true" loading="lazy" data-art={name} />;
}

export function MessagePhoto({ message, scope, onRead, reading }: Props) {
  const { message_id, photo_url } = message;
  // 进度按档说：checking（I108，问照片那一条）和 processing 同一档「冲洗中」——插图、那句字都按这一档（photoStage）
  const photo_status = photoStage(message.photo_status);
  // The API has a historical null-status paper postcard branch. Non-ready URLs are contradictory input.
  const paper = photo_status == null;
  if (photo_url && (paper || photo_status === 'ready')) return <PhotoImage key={JSON.stringify([scope, message_id, photo_url, photo_status ?? null])} url={photo_url} paper={paper} onRead={onRead} reading={reading} />;
  const text = photo_status === 'processing' ? '随信的照片还在冲洗'
    : photo_status === 'unknown' ? '这张照片还没确认'
    : photo_status === 'failed' ? '随信的照片没冲洗出来，信已经送到'
    : photo_status === 'ready' ? '这条来信暂时没有可显示的附图。' : null;
  if (!text) return null;
  return <div className="ps-msg__photo-pending ps-msg__attachment"><PendingArt status={photo_status} /><div role="status" aria-live="polite">{text}</div>{photo_status === 'ready' && onRead ? <button type="button" className="ps-msg__photo-read" disabled={reading} onClick={onRead}>重新读取消息</button> : null}</div>;
}
